
import React, { useState, useCallback, memo, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Globe, Languages, Search } from 'lucide-react';
import { appLanguages, appCountries } from '@/modules/app/utils/appLocalization';
import { InlineLoadingIndicator } from '@/modules/app/components/ui/LoadingStates';
import { SearchableCombobox } from '@/components/ui/searchable-combobox';

interface KeywordSearchFormProps {
  onSearch: (keyword: string, languageName: string, countryName: string) => void;
  isSearching: boolean;
  loadingMessage?: string; // Enhanced loading message prop
  searchPhase?: 'workspace' | 'search' | 'processing'; // New phase prop for enhanced styling
  initialKeyword?: string;
  initialLanguage?: string;
  initialCountry?: string;
}

const KeywordSearchForm: React.FC<KeywordSearchFormProps> = ({ 
  onSearch, 
  isSearching, 
  loadingMessage,
  searchPhase = 'processing',
  initialKeyword = '',
  initialLanguage = 'English',
  initialCountry = 'United States'
}) => {
  const [keyword, setKeyword] = useState(initialKeyword);
  const [languageName, setLanguageName] = useState(initialLanguage);
  const [countryName, setCountryName] = useState(initialCountry);

  // Update form values when initial props change
  useEffect(() => {
    setKeyword(initialKeyword);
    setLanguageName(initialLanguage);
    setCountryName(initialCountry);
  }, [initialKeyword, initialLanguage, initialCountry]);

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    if (!keyword.trim()) return;
    onSearch(keyword.trim(), languageName, countryName);
  }, [keyword, languageName, countryName, onSearch]);

  // Enhanced button text with phase-specific messaging
  const getButtonText = () => {
    if (!isSearching) return "Go!";
    
    if (loadingMessage) {
      // Extract key action from loading message for button
      if (loadingMessage.includes('Creating workspace')) return 'Creating...';
      if (loadingMessage.includes('Starting')) return 'Starting...';
      if (loadingMessage.includes('Processing')) return 'Processing...';
    }
    
    return "Searching...";
  };

  return (
    <form onSubmit={handleSubmit} className="w-full">
      <div className="flex flex-col md:flex-row gap-3 items-stretch">
        {/* Language Selector with SearchableCombobox */}
        <SearchableCombobox
          items={appLanguages}
          value={languageName}
          onValueChange={setLanguageName}
          placeholder="Language"
          searchPlaceholder="Search languages..."
          emptyMessage="No languages found."
          className="md:w-[160px]"
          disabled={isSearching}
          icon={<Languages className="h-4 w-4" />}
          aria-label="Select search language"
          aria-describedby="language-help"
        />
        {/* Hidden helper text for screen readers */}
        <div id="language-help" className="sr-only">
          Choose the language for keyword research
        </div>

        {/* Keyword Input with enhanced styling and accessibility */}
        <div className="flex-1 min-w-0">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4 z-10" aria-hidden="true" />
            <Input
              type="text"
              placeholder="Enter a keyword or phrase..."
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              className="h-12 pl-10 border-input bg-card"
              disabled={isSearching}
              aria-label="Keyword search input"
              aria-describedby="keyword-help"
              required
            />
          </div>
          {/* Hidden helper text for screen readers */}
          <div id="keyword-help" className="sr-only">
            Enter the main keyword or phrase you want to research
          </div>
        </div>
        
        {/* Country Selector with SearchableCombobox */}
        <SearchableCombobox
          items={appCountries}
          value={countryName}
          onValueChange={setCountryName}
          placeholder="Country"
          searchPlaceholder="Search countries..."
          emptyMessage="No countries found."
          className="md:w-[160px]"
          disabled={isSearching}
          icon={<Globe className="h-4 w-4" />}
          aria-label="Select target country"
          aria-describedby="country-help"
        />
        {/* Hidden helper text for screen readers */}
        <div id="country-help" className="sr-only">
          Choose the target country for keyword research
        </div>
        
        {/* Enhanced Button with Color Kit primary styling and accessibility */}
        <Button 
          type="submit" 
          size="lg" 
          className="h-12 w-full md:w-auto px-8 bg-primary text-primary-foreground hover:bg-primary/90 focus:ring-2 focus:ring-ring focus:ring-offset-2" 
          disabled={!keyword.trim() || isSearching}
          aria-describedby="submit-help"
        >
          {getButtonText()}
        </Button>
        {/* Hidden helper text for screen readers */}
        <div id="submit-help" className="sr-only">
          {isSearching 
            ? "Search is in progress, please wait" 
            : "Start keyword research with the selected parameters"
          }
        </div>
      </div>
      
      {/* Enhanced loading indicator with accessibility */}
      {isSearching && loadingMessage && (
        <div className="mt-4" role="status" aria-live="polite">
          <InlineLoadingIndicator 
            message={loadingMessage}
            phase={searchPhase}
          />
          <div className="sr-only">
            {loadingMessage}
          </div>
        </div>
      )}
    </form>
  );
};

// Memoize the component to prevent re-renders
// Only re-render if onSearch reference or isSearching state changes
export default memo(KeywordSearchForm);
